import { useMutation } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CategoryIcon, IconCamera, IconClose, IconPaperclip } from '../components/icons';
import { DetailHeader } from '../components/layout';
import { categoryName } from '../components/receipts';
import {
  AmountInput,
  Button,
  Card,
  Field,
  Input,
  Segmented,
  Textarea,
  useToast,
} from '../components/ui';
import { useI18n } from '../i18n';
import { api, type PaymentMethod } from '../lib/api';
import { useLedgerContext } from '../lib/ledger-context';
import { useCategories, useCreateExpense, usePeople } from '../lib/queries';
import { accentVar, cn, parseAmountInput } from '../lib/ui';
import { useErrorMessage } from './Auth';

const PAYMENT_METHODS: PaymentMethod[] = ['out_of_pocket', 'hsa_card', 'other'];

/** The help text keys are camelCase; the API values are snake_case. */
export const PAYMENT_HELP = {
  out_of_pocket: 'payment.outOfPocketHelp',
  hsa_card: 'payment.hsaCardHelp',
  other: 'payment.otherHelp',
} as const;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A single tappable option in a row of chips. */
function OptionChip({
  label,
  active,
  color,
  icon,
  onClick,
}: {
  label: string;
  active: boolean;
  color?: string;
  icon?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'press inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[14px] transition-colors',
        active
          ? 'border-transparent text-white'
          : 'border-[var(--line)] text-[var(--ink-secondary)] hover:bg-[var(--surface-sunken)]',
      )}
      style={active && color ? { background: color } : undefined}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * The capture step, first because that is the order things happen in real life:
 * you are holding a receipt, so photograph it, then fill in what it says.
 */
function AttachmentPicker({
  files,
  onAdd,
  onRemove,
}: {
  files: File[];
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
}) {
  const { t } = useI18n();
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  return (
    <div>
      <div className="flex gap-2.5">
        {/* `capture="environment"` opens the rear camera straight away on a
            phone; on a laptop the browser ignores it and shows a file picker. */}
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          onChange={(event) => {
            onAdd(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
        />
        <input
          ref={fileInput}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="sr-only"
          onChange={(event) => {
            onAdd(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
        />

        <Button
          type="button"
          variant="secondary"
          fullWidth
          icon={<IconCamera size={18} />}
          onClick={() => cameraInput.current?.click()}
        >
          {t('form.takePhoto')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          fullWidth
          icon={<IconPaperclip size={18} />}
          onClick={() => fileInput.current?.click()}
        >
          {t('form.chooseFile')}
        </Button>
      </div>

      <AnimatePresence>
        {files.length > 0 ? (
          <motion.ul
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 flex gap-2.5 overflow-x-auto pb-1"
          >
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`} className="relative shrink-0">
                {file.type.startsWith('image/') ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="h-20 w-20 rounded-[12px] border border-[var(--line)] object-cover"
                  />
                ) : (
                  <span className="flex h-20 w-20 items-center justify-center rounded-[12px] border border-[var(--line)] bg-[var(--surface-sunken)] text-[11px] text-[var(--ink-secondary)]">
                    PDF
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  aria-label={t('common.remove')}
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-[var(--ink)] p-1 text-[var(--ink-inverse)]"
                >
                  <IconClose size={12} strokeWidth={2.4} />
                </button>
              </li>
            ))}
          </motion.ul>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export function AddReceiptScreen({ paperlessReady }: { paperlessReady: boolean }) {
  const { t, locale, currency } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const toMessage = useErrorMessage();

  const { writable, currentId } = useLedgerContext();
  const categories = useCategories();
  const createExpense = useCreateExpense();

  // Default to the ledger currently in view; otherwise the only one that can
  // be written to, so a person with a single ledger never sees this control.
  //
  // Derived rather than seeded into state: the ledgers arrive from a query, so
  // on the very first render there are none to choose from. Freezing that
  // moment left ledgerId permanently empty, which disabled Save with nothing
  // on screen to explain why. Only an explicit choice is stored, and it is
  // ignored once it stops being writable.
  const [chosenLedgerId, setChosenLedgerId] = useState<string | null>(null);
  const ledgerId =
    chosenLedgerId && writable.some((l) => l.id === chosenLedgerId)
      ? chosenLedgerId
      : currentId && writable.some((l) => l.id === currentId)
        ? currentId
        : (writable[0]?.id ?? '');
  const setLedgerId = setChosenLedgerId;
  const people = usePeople(ledgerId || undefined);

  const [files, setFiles] = useState<File[]>([]);
  const [amount, setAmount] = useState('');
  const [personId, setPersonId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [serviceDate, setServiceDate] = useState(todayIso());
  const [provider, setProvider] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('out_of_pocket');
  const [showMore, setShowMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountCents = parseAmountInput(amount);
  const canSave =
    amountCents !== null && amountCents > 0 && Boolean(serviceDate) && Boolean(ledgerId);

  const currencySymbol =
    new Intl.NumberFormat(locale, { style: 'currency', currency })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value ?? currency;

  const reset = () => {
    setFiles([]);
    setAmount('');
    setProvider('');
    setDescription('');
    setNotes('');
    setShowMore(false);
  };

  const save = useMutation({
    mutationFn: async (mode: 'close' | 'again') => {
      const { expense } = await createExpense.mutateAsync({
        ledgerId,
        amountCents,
        personId,
        categoryId,
        serviceDate,
        provider: provider.trim() || null,
        description: description.trim() || null,
        notes: notes.trim() || null,
        paymentMethod,
      });

      // Uploads happen after the receipt exists, and their failure never loses
      // the numbers the person just typed.
      const failed: string[] = [];
      for (const file of files) {
        try {
          await api.uploadDocument(expense.id, file);
        } catch {
          failed.push(file.name);
        }
      }

      return { expenseId: expense.id, mode, failed };
    },
    onSuccess: ({ expenseId, mode, failed }) => {
      if (failed.length > 0) toast.show(t('detail.documentFailed'), 'critical');
      if (mode === 'again') {
        toast.show(t('common.saved'), 'positive');
        reset();
      } else {
        navigate(`/receipts/${expenseId}`, { replace: true });
      }
    },
    onError: (cause) => setError(toMessage(cause)),
  });

  return (
    <>
      <DetailHeader title={t('form.newReceipt')} onBack={() => navigate(-1)} />

      <form
        className="space-y-4 px-4 pb-24 pt-4 lg:px-8 lg:pb-8"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSave) save.mutate('close');
        }}
      >
        {paperlessReady ? (
          <Card>
            <div className="mb-3 text-[13px] font-medium text-[var(--ink-secondary)]">
              {t('form.attachDocument')}
            </div>
            <AttachmentPicker
              files={files}
              onAdd={(added) => setFiles((current) => [...current, ...added])}
              onRemove={(index) => setFiles((current) => current.filter((_, i) => i !== index))}
            />
          </Card>
        ) : null}

        <Card>
          <div className="mb-2 text-[13px] font-medium text-[var(--ink-secondary)]">
            {t('form.amount')}
          </div>
          <AmountInput
            value={amount}
            onChange={setAmount}
            currencySymbol={currencySymbol}
            autoFocus
          />
        </Card>

        {/* Only shown when there is genuinely a choice — one ledger means no
            control at all rather than a select with a single option. */}
        {writable.length > 1 ? (
          <Card>
            <div className="mb-3 text-[13px] font-medium text-[var(--ink-secondary)]">
              {t('ledger.singular')}
            </div>
            <div className="flex flex-wrap gap-2">
              {writable.map((ledger) => (
                <OptionChip
                  key={ledger.id}
                  label={ledger.name}
                  color={accentVar(ledger.color)}
                  active={ledgerId === ledger.id}
                  onClick={() => {
                    setLedgerId(ledger.id);
                    // People belong to a ledger, so a stale selection would
                    // otherwise be filed against the wrong person.
                    setPersonId(null);
                  }}
                />
              ))}
            </div>
          </Card>
        ) : null}

        {(people.data?.people.length ?? 0) > 0 ? (
          <Card>
            <div className="mb-3 text-[13px] font-medium text-[var(--ink-secondary)]">
              {t('form.person')}
            </div>
            <div className="flex flex-wrap gap-2">
              {people.data?.people
                .filter((person) => !person.archivedAt)
                .map((person) => (
                  <OptionChip
                    key={person.id}
                    label={person.name}
                    color={accentVar(person.color)}
                    active={personId === person.id}
                    onClick={() => setPersonId(personId === person.id ? null : person.id)}
                  />
                ))}
            </div>
          </Card>
        ) : null}

        <Card>
          <div className="mb-3 text-[13px] font-medium text-[var(--ink-secondary)]">
            {t('form.category')}
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.data?.categories
              .filter((category) => !category.archivedAt)
              .map((category) => (
                <OptionChip
                  key={category.id}
                  label={categoryName(category, locale)}
                  color={accentVar(category.color)}
                  active={categoryId === category.id}
                  icon={<CategoryIcon glyph={category.icon} size={15} />}
                  onClick={() => setCategoryId(categoryId === category.id ? null : category.id)}
                />
              ))}
          </div>
        </Card>

        <Card className="space-y-4">
          <Field label={t('form.serviceDate')}>
            <Input
              type="date"
              required
              value={serviceDate}
              max={todayIso()}
              onChange={(event) => setServiceDate(event.target.value)}
            />
          </Field>

          <Field label={t('form.provider')} optional>
            <Input
              value={provider}
              placeholder={t('form.providerPlaceholder')}
              onChange={(event) => setProvider(event.target.value)}
            />
          </Field>

          <Field label={t('payment.label')} hint={t(PAYMENT_HELP[paymentMethod])}>
            <Segmented
              className="w-full"
              value={paymentMethod}
              onChange={setPaymentMethod}
              options={PAYMENT_METHODS.map((method) => ({
                value: method,
                label: t(`payment.${method}` as 'payment.out_of_pocket'),
              }))}
            />
          </Field>
        </Card>

        {showMore ? (
          <Card className="space-y-4">
            <Field label={t('form.description')} optional>
              <Input
                value={description}
                placeholder={t('form.descriptionPlaceholder')}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Field>
            <Field label={t('form.notes')} optional>
              <Textarea
                value={notes}
                placeholder={t('form.notesPlaceholder')}
                onChange={(event) => setNotes(event.target.value)}
              />
            </Field>
          </Card>
        ) : (
          <button
            type="button"
            onClick={() => setShowMore(true)}
            className="w-full py-1 text-center text-[14px] text-[var(--accent)]"
          >
            {t('common.more')}
          </button>
        )}

        {error ? <p className="text-[13px] text-[var(--critical)]">{error}</p> : null}

        {/*
          A fixed action bar, so Save is reachable without scrolling to the
          bottom of a long form. It floats above the tab bar, and the form
          carries matching bottom padding so it never covers a field — the
          reason this is fixed rather than sticky.
        */}
        <div className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom,0px)+52px)] z-30 border-t border-[var(--line)] bg-[var(--surface)] px-4 py-3 lg:static lg:border-0 lg:bg-transparent lg:px-0">
          <div className="mx-auto flex max-w-3xl gap-2.5 lg:max-w-none">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              disabled={!canSave}
              loading={save.isPending && save.variables === 'again'}
              onClick={() => save.mutate('again')}
            >
              {t('form.addAnother')}
            </Button>
            <Button
              type="submit"
              fullWidth
              disabled={!canSave}
              loading={save.isPending && save.variables === 'close'}
            >
              {t('form.saveReceipt')}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
